import React from 'react';

import {
  Add as AddIcon,
  ListAlt as ListAltIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { Button } from '@mui/material';
import { generatePath, useNavigate } from 'react-router';

import { appRoutes, DateFormatPatterns, RouterEntitiesId } from '@clearverify/constants';
import { formatDate } from '@clearverify/utils';

import { type IntegrationInfoFragment } from 'src/api';
import { UserStatusChip } from 'src/components/common';
import {
  Table,
  type TableColumn,
  type TableFilter,
  type TableRowAction,
  TableTopAction,
} from 'src/components/table';
import {
  INTEGRATION_SCOPE_FILTER_OPTIONS,
  INTEGRATION_SCOPE_LABELS,
  INTEGRATION_STATUS_FILTER_OPTIONS,
} from 'src/constants/integration';
import { ModalId } from 'src/constants/modals';
import { useFilterStorage, useSearch } from 'src/hooks/common';
import { useModalV2 } from 'src/hooks/shared';
import { CreateIntegrationModal, ManageIntegrationModal } from 'src/modals/apiIntegrations';
import type { PaginationContextValue } from 'src/providers/PaginationProvider';
import { formatRateLimit } from 'src/utils/integrations';

type InboundIntegrationsTableProps = {
  integrationsList: IntegrationInfoFragment[];
  integrationsCount: number;
  onRefresh?: () => Promise<void>;
  pagination: PaginationContextValue;
  loading: boolean;
};

export const InboundIntegrationsTable: React.FC<InboundIntegrationsTableProps> = ({
  integrationsList,
  integrationsCount,
  onRefresh,
  pagination,
  loading,
}) => {
  const { openModal } = useModalV2();
  const { setSearchValue, searchValue } = useSearch();
  const { setSortOption } = useFilterStorage();
  const navigate = useNavigate();

  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect must not depend on "pagination" here
  React.useEffect(() => {
    pagination.resetPage();
  }, [searchValue]);

  const rowActions = React.useMemo(
    (): Array<TableRowAction<IntegrationInfoFragment>> => [
      {
        startIcon: <ListAltIcon />,
        actionName: 'Usage Logs',
        onAction: integration =>
          navigate(
            generatePath(appRoutes.apiIntegrations.usageLogs, {
              [RouterEntitiesId.integration]: integration.id,
            }),
          ),
      },
      {
        startIcon: <SettingsIcon />,
        actionName: 'Manage',
        onAction: integration =>
          openModal(ManageIntegrationModal, { id: ModalId.ManageIntegrationModal, integration }),
      },
    ],
    [navigate, openModal],
  );

  const topActions = React.useMemo(
    (): Array<TableTopAction> => [
      {
        actionName: 'Add',
        onAction: () => {
          openModal(CreateIntegrationModal, { id: ModalId.CreateIntegrationModal });
        },
        icon: <AddIcon />,
        show: true,
        render() {
          return (
            <Button
              variant="contained"
              startIcon={this.icon}
              disabled={loading}
              loading={loading}
              onClick={this.onAction}
            >
              New
            </Button>
          );
        },
      },
    ],
    [loading, openModal],
  );

  return (
    <Table
      dataSource={integrationsList}
      rowCount={integrationsCount}
      columns={columns}
      rowActions={rowActions}
      onSearch={setSearchValue}
      onSort={setSortOption}
      pagination={pagination}
      filters={filters}
      onRefresh={onRefresh}
      loading={loading}
      topActions={topActions}
    />
  );
};

const columns: Array<TableColumn<IntegrationInfoFragment>> = [
  {
    title: 'Name',
    key: 'name',
    sortable: true,
    nonNullSortable: true,
    render: (_value, integration) => integration.name,
  },
  {
    title: 'Status',
    key: 'status',
    sortable: true,
    nonNullSortable: true,
    render: (_value, integration) => <UserStatusChip userStatus={integration.status} />,
  },
  {
    title: 'Scopes',
    key: 'scope',
    render: (_value, integration) =>
      integration.scope
        .map(scope => INTEGRATION_SCOPE_LABELS[scope])
        .toSorted()
        .join(', ') || '-',
  },
  {
    title: 'IP rate limit',
    key: 'ipLimit',
    render: (_value, integration) =>
      formatRateLimit(integration.ipLimitAttempts, integration.ipLimitWindow),
  },
  {
    title: 'Credentials rate limit',
    key: 'credentialsLimit',
    render: (_value, integration) =>
      formatRateLimit(integration.credentialsLimitAttempts, integration.credentialsLimitWindow),
  },
  {
    title: 'Verifier rate limit',
    key: 'verifierLimit',
    render: (_value, integration) =>
      formatRateLimit(integration.verifierLimitAttempts, integration.verifierLimitWindow),
  },
  {
    title: 'Updated At',
    key: 'updatedAt',
    sortable: true,
    nonNullSortable: true,
    render: (_value, integration) =>
      formatDate(integration.updatedAt, DateFormatPatterns.fullDateWithTime),
  },
];

const filters: TableFilter[] = [
  {
    id: 'status',
    type: 'select',
    fieldProps: {
      label: 'Status',
      multiple: true,
      options: INTEGRATION_STATUS_FILTER_OPTIONS,
    },
  },
  {
    id: 'scope',
    type: 'select',
    fieldProps: {
      label: 'Scope',
      multiple: true,
      options: INTEGRATION_SCOPE_FILTER_OPTIONS,
    },
  },
];
